import { z } from "zod";

/**
 * Vendors we recognize for custom MCP server connections so they can be shown
 * with a brand icon instead of the generic one. Prefixed to keep these distinct
 * from the unrelated `github-repo`/`gitlab-repo` tool icons.
 */
export const KnownMcpVendorLiteral = z.enum([
  "mcp-vendor:amplitude",
  "mcp-vendor:atlassian",
  "mcp-vendor:attio",
  "mcp-vendor:datadog",
  "mcp-vendor:github",
  "mcp-vendor:gitlab",
  "mcp-vendor:glean",
  "mcp-vendor:grain",
  "mcp-vendor:granola",
  "mcp-vendor:launchdarkly",
  "mcp-vendor:mixpanel",
  "mcp-vendor:posthog",
  "mcp-vendor:pylon",
  "mcp-vendor:sentry",
  "mcp-vendor:slack",
  "mcp-vendor:stripe",
  "mcp-vendor:supabase",
]);
export type KnownMcpVendor = z.infer<typeof KnownMcpVendorLiteral>;

export type KnownMcpVendorMatcher = {
  readonly vendor: KnownMcpVendor;
  /**
   * Matched against the server URL host exactly or as a parent domain, so
   * `example.com` also matches `mcp.example.com`.
   */
  readonly hostnames?: readonly string[];
  /**
   * Matched as a prefix of the server URL path, on any host, and independently
   * of `hostnames` — either kind of match identifies the vendor. Only
   * appropriate for a path specific enough to identify the vendor by itself:
   * GitLab serves its MCP server from `/api/v4/mcp` on every self-managed
   * instance, so the path is what recognizes those.
   */
  readonly pathPrefixes?: readonly string[];
};

const KNOWN_MCP_VENDOR_MATCHERS: readonly KnownMcpVendorMatcher[] = [
  {
    vendor: "mcp-vendor:amplitude",
    hostnames: ["amplitude.com"],
  },
  {
    vendor: "mcp-vendor:atlassian",
    hostnames: ["atlassian.com", "atlassian.net"],
  },
  {
    vendor: "mcp-vendor:attio",
    hostnames: ["attio.com"],
  },
  {
    vendor: "mcp-vendor:datadog",
    hostnames: ["datadoghq.com", "datadoghq.eu", "ddog-gov.com"],
  },
  {
    vendor: "mcp-vendor:github",
    hostnames: ["githubcopilot.com", "github.com"],
  },
  {
    vendor: "mcp-vendor:gitlab",
    hostnames: ["gitlab.com"],
    pathPrefixes: ["/api/v4/mcp"],
  },
  {
    vendor: "mcp-vendor:glean",
    hostnames: ["glean.com"],
    pathPrefixes: ["/mcp/default"],
  },
  {
    vendor: "mcp-vendor:grain",
    hostnames: ["grain.com"],
  },
  {
    vendor: "mcp-vendor:granola",
    hostnames: ["granola.ai"],
  },
  {
    vendor: "mcp-vendor:launchdarkly",
    hostnames: ["launchdarkly.com", "launchdarkly.us"],
  },
  {
    vendor: "mcp-vendor:mixpanel",
    hostnames: ["mixpanel.com"],
  },
  {
    vendor: "mcp-vendor:posthog",
    hostnames: ["posthog.com"],
  },
  {
    vendor: "mcp-vendor:pylon",
    hostnames: ["usepylon.com"],
  },
  {
    vendor: "mcp-vendor:sentry",
    hostnames: ["sentry.dev", "sentry.io"],
  },
  {
    vendor: "mcp-vendor:slack",
    hostnames: ["slack.com"],
  },
  {
    vendor: "mcp-vendor:stripe",
    hostnames: ["stripe.com"],
  },
  {
    vendor: "mcp-vendor:supabase",
    hostnames: ["supabase.com", "supabase.co"],
  },
];

const matchesHostname = (host: string, hostname: string): boolean =>
  host === hostname || host.endsWith(`.${hostname}`);

/**
 * Best-effort identification of the vendor behind a custom MCP server URL.
 * Hostname matches win over path-only matches so that a more specific entry is
 * never shadowed by a generic one.
 */
export function resolveKnownMcpVendor(
  serverUrl: string,
): KnownMcpVendor | undefined {
  let url: URL;
  try {
    url = new URL(serverUrl);
  } catch {
    return undefined;
  }
  const host = url.hostname.toLowerCase().replace(/\.$/, "");
  const path = url.pathname.toLowerCase();

  const hostnameMatch = KNOWN_MCP_VENDOR_MATCHERS.find(
    (m) =>
      m.hostnames?.some((hostname) => matchesHostname(host, hostname)) === true,
  );
  if (hostnameMatch != null) {
    return hostnameMatch.vendor;
  }

  return KNOWN_MCP_VENDOR_MATCHERS.find(
    (m) => m.pathPrefixes?.some((prefix) => path.startsWith(prefix)) === true,
  )?.vendor;
}
